import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  Link,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { financeAPI } from '../../services/api';
import { formatCurrency } from './formatCurrency';
import StatusBadge from './StatusBadge';

function formatInvoiceCycleLabel(inv) {
  if (!inv) return '—';
  const start = inv.cycle_start ? String(inv.cycle_start).slice(0, 10) : '';
  const end = inv.cycle_end ? String(inv.cycle_end).slice(0, 10) : '';
  if (start && end) return `${start} → ${end}`;
  return inv.id || '—';
}

function emptyForm() {
  return {
    invoiceId: '',
    draftLocalId: null,
    invoiceNumber: '',
    amount: '',
    name: '',
    email: '',
    contact: '',
    description: '',
    comment: '',
    terms: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zipcode: '',
    country: '',
    lineItems: [],
    razorpayEmail: false,
    razorpaySms: false,
    ourEmail: false,
  };
}

function formToPayload(form) {
  const lineItems = (form.lineItems || []).map((l) => ({
    name: l.name,
    description: l.description || '',
    amountMajor: Number(l.amountMajor) || 0,
    kind: l.kind || 'tuition',
  }));
  const sum = Math.round(lineItems.reduce((s, l) => s + l.amountMajor, 0) * 100) / 100;
  return {
    invoiceNumber: String(form.invoiceNumber || '').trim(),
    amount: form.amount ? Number(form.amount) : sum,
    description: form.description,
    comment: form.comment,
    terms: form.terms,
    billingAddress: {
      line1: form.line1,
      line2: form.line2,
      city: form.city,
      state: form.state,
      zipcode: form.zipcode,
      country: form.country,
    },
    lineItems,
    customer: {
      name: form.name || null,
      email: form.email || null,
      contact: form.contact || null,
    },
    channels: {
      razorpayEmail: form.razorpayEmail,
      razorpaySms: form.razorpaySms,
      ourEmail: form.ourEmail,
    },
    draftLocalId: form.draftLocalId || undefined,
  };
}

/**
 * Razorpay invoice controls for finance subscription detail.
 * Hidden entirely when Razorpay is not operational.
 */
export default function RazorpayInvoicePanel({
  subscriptionId,
  currency,
  invoices = [],
  payments = [],
  canManage = false,
}) {
  const queryClient = useQueryClient();
  const [sendOpen, setSendOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [selectedLocalId, setSelectedLocalId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [invoiceNumberError, setInvoiceNumberError] = useState('');
  const [composeLoading, setComposeLoading] = useState(false);

  const statusQuery = useQuery(
    ['razorpayStatus'],
    async () => {
      const res = await financeAPI.getRazorpayStatus();
      return res.data?.data;
    },
    { staleTime: 60_000 }
  );

  const operational = Boolean(statusQuery.data?.operational && statusQuery.data?.schemaReady);

  const listQuery = useQuery(
    ['razorpayInvoices', subscriptionId],
    async () => {
      const res = await financeAPI.listRazorpayInvoices(subscriptionId);
      return res.data?.data || [];
    },
    { enabled: !!subscriptionId && operational }
  );

  const openInvoices = useMemo(
    () =>
      (invoices || []).filter((inv) => ['open', 'partially_paid'].includes(String(inv.status))),
    [invoices]
  );

  const rzpRows = useMemo(() => listQuery.data || [], [listQuery.data]);
  const activeByInvoice = useMemo(() => {
    const map = {};
    for (const r of rzpRows) {
      if (['draft', 'issued', 'partially_paid'].includes(String(r.status))) {
        map[r.subscription_invoice_id] = r;
      }
    }
    return map;
  }, [rzpRows]);

  const applyCompose = useCallback((data, billingInvoiceId) => {
    const draft = data.existingDraft;
    const addr = data.billingAddress || {};
    const customer = data.customer || {};
    const hasEmail = Boolean(customer.email);
    const hasPhone = Boolean(customer.contact);
    const channels = data.channels || {};
    setForm({
      invoiceId: billingInvoiceId,
      draftLocalId: draft?.id || null,
      invoiceNumber: data.invoiceNumber || '',
      amount: data.amount != null ? String(data.amount) : '',
      name: customer.name || '',
      email: customer.email || '',
      contact: customer.contact || '',
      description: data.description || '',
      comment: data.comment || '',
      terms: data.terms || '',
      line1: addr.line1 || '',
      line2: addr.line2 || '',
      city: addr.city || '',
      state: addr.state || '',
      zipcode: addr.zipcode || '',
      country: addr.country || customer.country || '',
      lineItems: (data.lineItems || []).map((l) => ({
        name: l.name || '',
        description: l.description || '',
        amountMajor: String(l.amountMajor ?? ''),
        kind: l.kind || 'tuition',
      })),
      razorpayEmail: channels.razorpayEmail != null ? Boolean(channels.razorpayEmail) : hasEmail,
      razorpaySms: channels.razorpaySms != null ? Boolean(channels.razorpaySms) : hasPhone,
      ourEmail: channels.ourEmail != null ? Boolean(channels.ourEmail) : hasEmail,
    });
    setInvoiceNumberError('');
  }, []);

  const loadCompose = useCallback(
    async (billingInvoiceId, amountOverride) => {
      if (!billingInvoiceId) return;
      setComposeLoading(true);
      try {
        const params = {};
        if (amountOverride != null && amountOverride !== '') params.amount = amountOverride;
        const res = await financeAPI.composeRazorpayInvoice(subscriptionId, billingInvoiceId, params);
        applyCompose(res.data?.data || {}, billingInvoiceId);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load invoice prefill');
      } finally {
        setComposeLoading(false);
      }
    },
    [subscriptionId, applyCompose]
  );

  const openSend = async (prefillInvoiceId) => {
    const invoiceId = prefillInvoiceId || openInvoices[0]?.id || '';
    setForm(emptyForm());
    setPreviewOpen(false);
    setSendOpen(true);
    if (invoiceId) await loadCompose(invoiceId);
  };

  const openEditDraft = async (row) => {
    setSendOpen(true);
    setPreviewOpen(false);
    await loadCompose(row.subscription_invoice_id, row.amount);
  };

  const checkInvoiceNumber = async (value) => {
    const invoiceNumber = String(value || '').trim();
    if (!invoiceNumber) {
      setInvoiceNumberError('Invoice ID is required');
      return false;
    }
    if (invoiceNumber.length > 40) {
      setInvoiceNumberError('Max 40 characters');
      return false;
    }
    try {
      const res = await financeAPI.checkRazorpayInvoiceNumber({
        invoiceNumber,
        excludeLocalId: form.draftLocalId || undefined,
      });
      const available = Boolean(res.data?.data?.available);
      setInvoiceNumberError(available ? '' : `Invoice ID "${invoiceNumber}" is already used`);
      return available;
    } catch (_) {
      setInvoiceNumberError('Could not verify uniqueness');
      return false;
    }
  };

  const invalidateLists = () => {
    queryClient.invalidateQueries(['razorpayInvoices', subscriptionId]);
    queryClient.invalidateQueries(['financeSubPayments', subscriptionId]);
    queryClient.invalidateQueries(['financeSubInvoices', subscriptionId]);
  };

  const saveMutation = useMutation(
    async () => {
      const payload = formToPayload(form);
      if (form.draftLocalId) {
        const res = await financeAPI.updateRazorpayDraft(form.draftLocalId, payload);
        return { mode: 'update', body: res.data };
      }
      const res = await financeAPI.saveRazorpayDraft(subscriptionId, form.invoiceId, payload);
      return { mode: 'save', body: res.data };
    },
    {
      onSuccess: ({ mode, body }) => {
        toast.success(mode === 'update' ? 'Draft updated' : 'Draft saved');
        const id = body?.data?.id;
        if (id) setForm((p) => ({ ...p, draftLocalId: id }));
        setPreviewOpen(false);
        setSendOpen(false);
        invalidateLists();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to save draft');
      },
    }
  );

  const sendMutation = useMutation(
    async () => {
      const payload = formToPayload(form);
      const res = await financeAPI.createRazorpayInvoice(subscriptionId, form.invoiceId, payload);
      return res.data;
    },
    {
      onSuccess: (body) => {
        toast.success('Razorpay invoice issued');
        if (body?.data?.shortUrl) {
          toast.success(`Pay link: ${body.data.shortUrl}`, { duration: 6000 });
        }
        setPreviewOpen(false);
        setSendOpen(false);
        invalidateLists();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to create Razorpay invoice');
      },
    }
  );

  const notifyMutation = useMutation(
    async ({ localId, channels }) => {
      const res = await financeAPI.notifyRazorpayInvoice(localId, { channels });
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Notifications sent');
        setNotifyOpen(false);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Notify failed');
      },
    }
  );

  const cancelMutation = useMutation(
    async (localId) => {
      const res = await financeAPI.cancelRazorpayInvoice(localId);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Razorpay invoice cancelled');
        invalidateLists();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Cancel failed');
      },
    }
  );

  const refundMutation = useMutation(
    async (paymentId) => {
      const res = await financeAPI.refundRazorpayPayment(paymentId, {});
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Refund initiated');
        invalidateLists();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Refund failed');
      },
    }
  );

  useEffect(() => {
    if (!form.invoiceId || !sendOpen) return;
    // amount sync only when switching invoice via compose
  }, [form.invoiceId, sendOpen]);

  const lineTotal = useMemo(() => {
    return Math.round(
      (form.lineItems || []).reduce((s, l) => s + (Number(l.amountMajor) || 0), 0) * 100
    ) / 100;
  }, [form.lineItems]);

  if (statusQuery.isLoading) return null;
  if (!operational) return null;
  if (!canManage) return null;

  const channelFields = (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.razorpayEmail}
              disabled={!form.email}
              onChange={(e) => setForm((p) => ({ ...p, razorpayEmail: e.target.checked }))}
            />
          }
          label="Razorpay email"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.razorpaySms}
              disabled={!form.contact}
              onChange={(e) => setForm((p) => ({ ...p, razorpaySms: e.target.checked }))}
            />
          }
          label="Razorpay SMS"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.ourEmail}
              disabled={!form.email}
              onChange={(e) => setForm((p) => ({ ...p, ourEmail: e.target.checked }))}
            />
          }
          label="Our email"
        />
      </Grid>
    </Grid>
  );

  const isLocalDraft = (r) => String(r.status) === 'draft' && !r.razorpay_invoice_id;

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="700">Razorpay Invoices</Typography>
          <Typography variant="caption" color="text.secondary">
            Payment request via Razorpay (not a GST tax invoice). Preview → Save / Update / Send.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          disabled={openInvoices.length === 0}
          onClick={() => openSend()}
        >
          Send Razorpay Invoice
        </Button>
      </Box>

      {listQuery.isLoading ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : rzpRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No Razorpay invoices yet.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice ID</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Pay link</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rzpRows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.invoice_number || '—'}</TableCell>
                  <TableCell>{r.created_at ? String(r.created_at).slice(0, 19).replace('T', ' ') : '—'}</TableCell>
                  <TableCell>{formatCurrency(r.amount, r.currency || currency)}</TableCell>
                  <TableCell>
                    <StatusBadge status={String(r.status)} />
                    {isLocalDraft(r) && <Chip size="small" label="local draft" sx={{ ml: 1 }} />}
                    {activeByInvoice[r.subscription_invoice_id]?.id === r.id && !isLocalDraft(r) && (
                      <Chip size="small" label="active" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {r.short_url ? (
                      <Link href={r.short_url} target="_blank" rel="noopener noreferrer">
                        Open
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isLocalDraft(r) && (
                      <>
                        <Button size="small" onClick={() => openEditDraft(r)}>Edit / Send</Button>
                        <Button
                          size="small"
                          color="warning"
                          onClick={() => {
                            if (window.confirm('Discard this local draft?')) {
                              cancelMutation.mutate(r.id);
                            }
                          }}
                        >
                          Discard
                        </Button>
                      </>
                    )}
                    {['issued', 'partially_paid'].includes(String(r.status)) && (
                      <>
                        <Button
                          size="small"
                          onClick={() => {
                            const snap = r.customer_snapshot || {};
                            setForm((p) => ({
                              ...p,
                              email: snap.email || p.email,
                              contact: snap.contact || p.contact,
                              name: snap.name || p.name,
                              razorpayEmail: Boolean(snap.email),
                              razorpaySms: Boolean(snap.contact),
                              ourEmail: Boolean(snap.email),
                            }));
                            setSelectedLocalId(r.id);
                            setNotifyOpen(true);
                          }}
                        >
                          Resend
                        </Button>
                        <Button
                          size="small"
                          color="warning"
                          onClick={() => {
                            if (window.confirm('Cancel this Razorpay invoice?')) {
                              cancelMutation.mutate(r.id);
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {r.short_url && (
                      <Button
                        size="small"
                        onClick={() => {
                          navigator.clipboard?.writeText(r.short_url);
                          toast.success('Link copied');
                        }}
                      >
                        Copy
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(payments || []).some(
        (p) =>
          p.gateway_name === 'razorpay' &&
          String(p.status) === 'paid' &&
          Number(p.amount) > 0 &&
          p.gateway_reference_id &&
          String(p.gateway_reference_id).startsWith('pay_')
      ) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Razorpay refunds</Typography>
          {(payments || [])
            .filter(
              (p) =>
                p.gateway_name === 'razorpay' &&
                String(p.status) === 'paid' &&
                Number(p.amount) > 0 &&
                p.gateway_reference_id &&
                String(p.gateway_reference_id).startsWith('pay_')
            )
            .map((p) => (
              <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2">
                  {formatCurrency(p.amount, p.currency || currency)} · {p.gateway_reference_id}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    if (window.confirm('Refund this Razorpay payment in full?')) {
                      refundMutation.mutate(p.id);
                    }
                  }}
                >
                  Refund
                </Button>
              </Box>
            ))}
        </Box>
      )}

      <Dialog open={sendOpen} onClose={() => setSendOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.draftLocalId ? 'Edit Razorpay draft' : 'Compose Razorpay Invoice'}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Prefill is editable. Enter Invoice ID, then Preview. Save keeps a local draft; Send creates &amp; issues on Razorpay.
          </Alert>
          {composeLoading ? (
            <Typography variant="body2">Loading prefill…</Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Billing invoice"
                  value={form.invoiceId}
                  onChange={(e) => loadCompose(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={Boolean(form.draftLocalId)}
                >
                  {openInvoices.map((inv) => {
                    const rem = Math.max(0, (Number(inv.amount_due) || 0) - (Number(inv.amount_paid) || 0));
                    const active = activeByInvoice[inv.id];
                    const blocked = Boolean(active && active.razorpay_invoice_id);
                    return (
                      <MenuItem key={inv.id} value={inv.id} disabled={blocked && inv.id !== form.invoiceId}>
                        {formatInvoiceCycleLabel(inv)} — {formatCurrency(rem, currency)}
                        {blocked ? ' (active Razorpay invoice exists)' : ''}
                        {active && !active.razorpay_invoice_id ? ' (has local draft)' : ''}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Invoice ID *"
                  value={form.invoiceNumber}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, invoiceNumber: e.target.value.slice(0, 40) }));
                    setInvoiceNumberError('');
                  }}
                  onBlur={(e) => checkInvoiceNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={Boolean(invoiceNumberError)}
                  helperText={invoiceNumberError || 'Max 40 characters; must be unique'}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Amount (override total)"
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={`Line total: ${formatCurrency(lineTotal, currency)}`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Currency" value={currency || ''} fullWidth size="small" disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  helperText="Format: Kid Name; Parent Name: Fee duration"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Customer name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Phone"
                  value={form.contact}
                  onChange={(e) => setForm((p) => ({ ...p, contact: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Billing address</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address line 1"
                  value={form.line1}
                  onChange={(e) => setForm((p) => ({ ...p, line1: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Address line 2"
                  value={form.line2}
                  onChange={(e) => setForm((p) => ({ ...p, line2: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  value={form.city}
                  onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="State"
                  value={form.state}
                  onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Zip / Postcode"
                  value={form.zipcode}
                  onChange={(e) => setForm((p) => ({ ...p, zipcode: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Country"
                  value={form.country}
                  onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Line items</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        lineItems: [
                          ...p.lineItems,
                          { name: '', description: '', amountMajor: '', kind: 'tuition' },
                        ],
                      }))
                    }
                  >
                    Add line
                  </Button>
                </Box>
                {(form.lineItems || []).map((line, idx) => (
                  <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Name"
                        value={line.name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((p) => {
                            const next = [...p.lineItems];
                            next[idx] = { ...next[idx], name: v };
                            return { ...p, lineItems: next };
                          });
                        }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Description"
                        value={line.description}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((p) => {
                            const next = [...p.lineItems];
                            next[idx] = { ...next[idx], description: v };
                            return { ...p, lineItems: next };
                          });
                        }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={10} sm={3}>
                      <TextField
                        label="Amount"
                        type="number"
                        value={line.amountMajor}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((p) => {
                            const next = [...p.lineItems];
                            next[idx] = { ...next[idx], amountMajor: v };
                            return { ...p, lineItems: next };
                          });
                        }}
                        fullWidth
                        size="small"
                        inputProps={{ step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={2} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        aria-label="Remove line"
                        onClick={() =>
                          setForm((p) => ({
                            ...p,
                            lineItems: p.lineItems.filter((_, i) => i !== idx),
                          }))
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Customer note"
                  value={form.comment}
                  onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Terms & conditions"
                  value={form.terms}
                  onChange={(e) => setForm((p) => ({ ...p, terms: e.target.value }))}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Notify channels</Typography>
                {channelFields}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            disabled={!form.invoiceId || composeLoading}
            onClick={async () => {
              const ok = await checkInvoiceNumber(form.invoiceNumber);
              if (!ok) return;
              setPreviewOpen(true);
            }}
          >
            Preview
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invoice preview</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">Invoice ID</Typography>
          <Typography variant="subtitle1" gutterBottom fontWeight={700}>{form.invoiceNumber}</Typography>
          <Typography variant="body2" color="text.secondary">Description</Typography>
          <Typography variant="body1" gutterBottom>{form.description || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">Customer</Typography>
          <Typography variant="body1" gutterBottom>
            {form.name || '—'} · {form.email || 'no email'} · {form.contact || 'no phone'}
          </Typography>
          <Typography variant="body2" color="text.secondary">Billing address</Typography>
          <Typography variant="body1" gutterBottom>
            {[form.line1, form.line2, form.city, form.state, form.zipcode, form.country].filter(Boolean).join(', ') || '—'}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(form.lineItems || []).map((l, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Typography variant="body2">{l.name}</Typography>
                    {l.description ? (
                      <Typography variant="caption" color="text.secondary">{l.description}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(Number(l.amountMajor) || 0, currency)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell align="right">
                  <strong>{formatCurrency(form.amount ? Number(form.amount) : lineTotal, currency)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" color="text.secondary">Customer note</Typography>
          <Typography variant="body2" gutterBottom sx={{ whiteSpace: 'pre-wrap' }}>{form.comment || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">Terms</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{form.terms || '—'}</Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setPreviewOpen(false)}>Back</Button>
          <Button
            variant="outlined"
            disabled={saveMutation.isLoading || !form.invoiceNumber}
            onClick={() => saveMutation.mutate()}
          >
            {form.draftLocalId ? 'Update' : 'Save'}
          </Button>
          <Button
            variant="contained"
            disabled={sendMutation.isLoading || !form.invoiceNumber}
            onClick={() => sendMutation.mutate()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={notifyOpen} onClose={() => setNotifyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Resend notifications</DialogTitle>
        <DialogContent dividers>{channelFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={notifyMutation.isLoading}
            onClick={() =>
              notifyMutation.mutate({
                localId: selectedLocalId,
                channels: {
                  razorpayEmail: form.razorpayEmail,
                  razorpaySms: form.razorpaySms,
                  ourEmail: form.ourEmail,
                },
              })
            }
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/** Soft warning helper for Record Payment when an active Razorpay invoice exists. */
export function useActiveRazorpayWarning(subscriptionId, invoiceId, enabled) {
  const { data } = useQuery(
    ['razorpayInvoices', subscriptionId],
    async () => {
      const res = await financeAPI.listRazorpayInvoices(subscriptionId);
      return res.data?.data || [];
    },
    { enabled: Boolean(enabled && subscriptionId) }
  );
  if (!invoiceId || !data) return false;
  return data.some(
    (r) =>
      String(r.subscription_invoice_id) === String(invoiceId) &&
      ['draft', 'issued', 'partially_paid'].includes(String(r.status))
  );
}
